/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from "react";
import { sortCbq } from "../utils/cbqOrder";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { QuizDTO } from "../api/Quiz/GetQuizById";
import { SubmitQuiz, QuizResponse, QuizSubmissionDTO } from "../api/Quiz/SubmitQuiz";
import {
  CBQSavedResponse,
  CBQStudentQuestion,
  CBQStudentScenario,
} from "../api/Quiz/cbqStudentTypes";
import { SaveCBQResponse, CBQResponseBody } from "../api/Quiz/SaveCBQResponse";
import { SubmitCBQSession } from "../api/Quiz/SubmitCBQSession";
import { StartCBQSession } from "../api/Quiz/StartCBQSession";
import { GetCBQSession } from "../api/Quiz/GetCBQSession";
import Spinner from "../components/Shared/Spinner";
import CbqQuestion from "../components/CbqExam/CbqQuestion";
import FinishSectionModal from "../components/CbqExam/FinishSectionModal";
import PostMcqExam from "../components/Quiz/PostMcqExam";
import Draggable from "../components/common/Draggable";
import HiddenPdfLoader from "../components/common/HiddenPdfLoader";
import { isNativeMode, withNativeParam } from "../lib/nativeMode";
import Calculator from "../components/Quiz/Calculator/Calculator";
import CalculatorIcon from "../components/Icons/CalculatorIcon";
import CloseIcon from "../components/Icons/CloseIcon";
import QuizClockIcon from "../components/Icons/QuizClockIcon";
import FlagIcon from "../components/Icons/FlagIcon";
import QuizArrowIcon from "../components/Icons/QuizArrowIcon";
import PdfQuizIcon from "../components/Icons/PdfQuizIcon";
import QuizFolderIcon from "../components/Icons/QuizFolderIcon";
import { useUserDataStore } from "../stores/user";
import Logo from "../assets/NWWhiteLogo.png";

type CbqItem =
  | { kind: "scenario"; scenario: CBQStudentScenario; scenarioIndex: number }
  | {
      kind: "question";
      q: CBQStudentQuestion;
      scenario: CBQStudentScenario;
      scenarioIndex: number;
      qNumber: number;
    };

// Ordering lives in the shared comparator (sortKey first, then `order`).
// Stable identity for a position in `items`. The current position is tracked
// by this key rather than by array index: `items` is re-sorted whenever the
// session payload changes, and if the ordering shifts, a stored index silently
// starts pointing at a different question — which is how answers ended up
// attributed to the wrong one.
const itemKey = (it: CbqItem) =>
  it.kind === "scenario" ? `s:${it.scenario.id}` : `q:${it.q.id}`;

const letter = (i: number) => String.fromCharCode(65 + i);

const formatTime = (t: number) => {
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(
    s
  ).padStart(2, "0")}`;
};

const RETRY_DELAYS = [0, 1500, 3000];

// Start the CBQ session, retrying transport failures. A 4xx is terminal: the
// exam is closed, already attempted, or the student isn't enrolled, and
// retrying only stalls them before the same error.
const startSessionWithRetry = async (cbqExamId: string) => {
  let last: Awaited<ReturnType<typeof StartCBQSession>> | undefined;
  for (const delay of RETRY_DELAYS) {
    if (delay) await new Promise((r) => setTimeout(r, delay));
    last = await StartCBQSession(cbqExamId);
    if (last.data?.id) return last;
    const status = last.error?.statusCode;
    if (status && status < 500) return last;
  }
  return last;
};

// One session per exam per page load. A component-level ref can't guarantee
// this: StrictMode's double-mount, HMR, and any parent remount all give the
// effect a fresh ref, and each extra run opens another session server-side.
// Module scope outlives all of them. A successful start is cached so a remount
// reuses the same id; a failure is evicted so Retry can start over.
const sessionStarts = new Map<
  string,
  Promise<Awaited<ReturnType<typeof startSessionWithRetry>>>
>();

const startSessionOnce = (cbqExamId: string) => {
  const existing = sessionStarts.get(cbqExamId);
  if (existing) return existing;
  const pending = startSessionWithRetry(cbqExamId).then((result) => {
    if (!result?.data?.id) sessionStarts.delete(cbqExamId);
    return result;
  });
  sessionStarts.set(cbqExamId, pending);
  return pending;
};

const bodyHasAnswer = (b: CBQResponseBody) =>
  !!(
    b.mcq ||
    b.multiResponse ||
    b.dropdown ||
    b.dragDropOrdering ||
    b.dragDropClassification ||
    b.numeric
  );

const MergedMcqCbqExam = ({
  quiz,
  cbqExamId,
  requiredScore,
}: {
  quiz: QuizDTO;
  cbqExamId: string;
  requiredScore: number;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useUserDataStore();
  const backTo = (location.state as { from?: string } | null)?.from;

  const [section, setSection] = useState<1 | 2>(1);
  const [mcqPassed, setMcqPassed] = useState(false);
  const [mcqResult, setMcqResult] = useState<QuizResponse | null>(null);
  const [mcqFailed, setMcqFailed] = useState(false);

  // The CBQ session opens as soon as the exam starts (this component mounts,
  // right after the student confirms details), then the session is fetched.
  // Opening it up front means it's ready long before the MCQ is submitted, so
  // a passing student never gets stranded by a session that fails to start.
  const [cbqSessionId, setCbqSessionId] = useState("");
  const { data: cbqSessionData, isLoading: cbqLoading, refetch: refetchCbqSession } = useQuery({
    queryFn: () => GetCBQSession(cbqSessionId),
    queryKey: ["cbq-session", cbqSessionId],
    enabled: !!cbqSessionId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const cbq = cbqSessionData?.data;
  console.log("[mergedCBQ] sessionId", cbqSessionId, "payload", cbq);

  // Best-effort: start the session the moment the exam begins. If it fails
  // here, submitMcq retries before grading, so this stays silent.
  // The ref guard keeps StrictMode's double-mount from opening two sessions.
  // Deliberately no cancel-on-unmount flag: StrictMode's throwaway unmount
  // would trip it and discard the id of a session the server already opened,
  // leaving the student with no session and an orphan row.
  // The exam is held behind a spinner until this resolves, so the student
  // can't burn clock on questions they might not be able to submit.
  const [sessionStarting, setSessionStarting] = useState(true);
  const [sessionStartError, setSessionStartError] = useState("");
  useEffect(() => {
    (async () => {
      const start = await startSessionOnce(cbqExamId);
      if (start?.data?.id) setCbqSessionId(start.data.id);
      else
        setSessionStartError(
          start?.error?.message || "Couldn't start the CBQ section."
        );
      setSessionStarting(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape hatch when the start above exhausted its retries. Only reachable
  // with no session open, so it can't produce a duplicate.
  const [retryingSession, setRetryingSession] = useState(false);
  const retrySession = async () => {
    setRetryingSession(true);
    const start = await startSessionOnce(cbqExamId);
    setRetryingSession(false);
    if (start?.data?.id) {
      setCbqSessionId(start.data.id);
      setSessionStartError("");
      setMcqSubmitError(false);
      return;
    }
    const message =
      start?.error?.message || "Still can't start the CBQ section.";
    setSessionStartError(message);
    toast.error(message);
  };

  // ---------------- MCQ section ----------------
  const mcqQuestions = quiz.questions ?? [];
  const [mcqCurrent, setMcqCurrent] = useState(0);
  const [mcqAnswers, setMcqAnswers] = useState<Record<number, string>>({});
  const [mcqFlagged, setMcqFlagged] = useState<Set<number>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submittingMcq, setSubmittingMcq] = useState(false);
  const [mcqSubmitError, setMcqSubmitError] = useState(false);

  const mcqTotal = quiz.duration || 120 * mcqQuestions.length;
  const [mcqRemaining, setMcqRemaining] = useState(mcqTotal);
  useEffect(() => {
    // Clock only runs once the session is open — see the gate below.
    if (section !== 1 || mcqResult || !cbqSessionId) return;
    const t = setInterval(() => {
      setMcqRemaining((p) => {
        if (p <= 1) {
          clearInterval(t);
          return 0;
        }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, mcqResult, cbqSessionId]);

  // Guards the manual (Finish Section) and time-up paths against each other:
  // `mcqResult` only lands after grading returns, so the clock hitting 0 while
  // a manual submit is still in flight would otherwise post a second attempt.
  const submitLockRef = useRef(false);

  const submitMcq = async () => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setSubmittingMcq(true);
    setMcqSubmitError(false);
    const submissions: QuizSubmissionDTO[] = mcqQuestions
      .map((q, i) =>
        mcqAnswers[i]
          ? {
              questionId: q.id,
              choiceId: mcqAnswers[i],
              marked: mcqFlagged.has(i),
            }
          : null
      )
      .filter(Boolean) as QuizSubmissionDTO[];
    try {
      // The session opened at exam start and is never started here — grading
      // must not mint a second one. If it's still missing, block the submit so
      // the trial isn't burned on an MCQ the student can't advance from.
      if (!cbqSessionId) {
        setMcqSubmitError(true);
        submitLockRef.current = false; // Nothing posted — the Retry button needs this path open
        toast.error("CBQ section isn't ready. Retry in a moment.");
        return;
      }

      const res: any = await SubmitQuiz(quiz.id, submissions);
      const result: QuizResponse = res?.data ?? res;
      setMcqResult(result);
      if ((result?.score ?? 0) >= requiredScore) {
        setMcqPassed(true);
        setSection(2);
        // The CBQ session was fetched at mount, before section 1 was passed, so
        // its payload has no section-2 scenarios (the server gates them until
        // the MCQ is cleared). staleTime would otherwise keep that empty copy,
        // leaving the CBQ section blank until a manual reload. Refetch now that
        // grading has unlocked section 2 server-side.
        refetchCbqSession();
      } else {
        setMcqFailed(true);
        toast.error(
          `Scored ${result?.score ?? 0}% — need ${requiredScore}% to unlock the CBQ`
        );
      }
    } catch (e: any) {
      submitLockRef.current = false; // Submit failed — allow another attempt
      toast.error(e?.response?.data?.message ?? "Failed to submit MCQ");
    } finally {
      setSubmittingMcq(false);
    }
  };

  // Time is up. mcqRemaining stays pinned at 0, so this fires once — a failed
  // session start leaves the student on the "Retry submit" button below.
  useEffect(() => {
    if (mcqRemaining === 0 && !mcqResult && !mcqSubmitError && section === 1)
      submitMcq();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mcqRemaining]);

  // ---------------- CBQ section ----------------
  const items: CbqItem[] = useMemo(() => {
    const scenarios = sortCbq(cbq?.scenarios ?? []);
    const out: CbqItem[] = [];
    let qn = 0;
    scenarios.forEach((scenario, scenarioIndex) => {
      out.push({ kind: "scenario", scenario, scenarioIndex });
      sortCbq(scenario.questions ?? [])
        .forEach((q) => {
          qn += 1;
          out.push({ kind: "question", q, scenario, scenarioIndex, qNumber: qn });
        });
    });
    return out;
  }, [cbq]);

  const [cbqCurrentKey, setCbqCurrentKey] = useState<string | null>(null);
  // Resolved fresh from `items` on every render, so a reorder moves the
  // selection with the question instead of leaving it on whatever now
  // occupies that slot.
  const cbqCurrent = useMemo(() => {
    if (!cbqCurrentKey) return 0;
    const i = items.findIndex((it) => itemKey(it) === cbqCurrentKey);
    return i >= 0 ? i : 0;
  }, [items, cbqCurrentKey]);
  const goToCbqIndex = (i: number) => {
    const it = items[i];
    if (it) setCbqCurrentKey(itemKey(it));
  };
  const [cbqAnswered, setCbqAnswered] = useState<Set<string>>(new Set());
  const [cbqFlagged, setCbqFlagged] = useState<Set<string>>(new Set());
  const [viewedScenarios, setViewedScenarios] = useState<Set<string>>(new Set());
  const [submittingCbq, setSubmittingCbq] = useState(false);

  const savedByQuestion = useMemo(() => {
    const map: Record<string, CBQSavedResponse> = {};
    (cbq?.responses ?? []).forEach((r) => {
      if (r?.cbqScenarioQuestionId) map[r.cbqScenarioQuestionId] = r;
    });
    return map;
  }, [cbq]);

  // Seed answered/flagged from what the server already holds. Without this
  // only answers saved during this mount count as answered, so a refetch or
  // remount showed work the student had already done as untouched — and
  // dragged the progress bar down with it. Merged into the existing set rather
  // than replacing it, so an answer saved locally isn't dropped by a payload
  // that predates it.
  useEffect(() => {
    const responses = cbq?.responses ?? [];
    if (!responses.length) return;
    setCbqAnswered((prev) => {
      const next = new Set(prev);
      responses.forEach((r) => {
        // A saved response carries the same answer fields as a request body
        // but no `type`, so the shapes are checked separately.
        const hasAnswer =
          r?.mcq ||
          r?.multiResponse ||
          r?.dropdown ||
          r?.dragDropOrdering ||
          r?.dragDropClassification ||
          r?.numeric;
        if (r?.cbqScenarioQuestionId && hasAnswer)
          next.add(r.cbqScenarioQuestionId);
      });
      return next;
    });
    setCbqFlagged((prev) => {
      const next = new Set(prev);
      responses.forEach((r) => {
        if (r?.cbqScenarioQuestionId && r.isFlagged)
          next.add(r.cbqScenarioQuestionId);
      });
      return next;
    });
  }, [cbq]);

  const startedMap = useMemo(() => {
    const m: Record<string, string> = {};
    (cbq?.scenarioProgress ?? []).forEach((p) => {
      if (p.scenarioId && p.startedAt) m[p.scenarioId] = p.startedAt;
    });
    return m;
  }, [cbq]);

  useEffect(() => {
    if (section !== 2) return;
    const it = items[cbqCurrent];
    if (it?.kind === "scenario") {
      setViewedScenarios((prev) =>
        prev.has(it.scenario.id) ? prev : new Set(prev).add(it.scenario.id)
      );
    }
  }, [cbqCurrent, items, section]);

  const [cbqRemaining, setCbqRemaining] = useState<number | null>(null);
  useEffect(() => {
    if (section !== 2) return;
    const it = items[cbqCurrent];
    const scenario = it?.scenario;
    const startedAt =
      (scenario && startedMap[scenario.id]) ?? cbq?.session?.section2StartedAt;
    const estMin = scenario?.estimatedMinutes;
    if (!startedAt || !estMin) {
      setCbqRemaining(null);
      return;
    }
    const deadline = new Date(startedAt).getTime() + estMin * 60000;
    const tick = () =>
      setCbqRemaining(Math.max(0, Math.floor((deadline - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [items, cbqCurrent, startedMap, cbq?.session?.section2StartedAt, section]);

  const saveCbq = async (questionId: string, body: CBQResponseBody) => {
    if (bodyHasAnswer(body))
      setCbqAnswered((prev) => new Set(prev).add(questionId));
    const res = await SaveCBQResponse(cbqSessionId, questionId, body);
    if (res.error) toast.error(res.error.message || "Could not save answer");
  };

  const submitCbq = async () => {
    setSubmittingCbq(true);
    const res = await SubmitCBQSession(cbqSessionId);
    setSubmittingCbq(false);
    if (res.error) {
      toast.error(res.error.message || "Could not submit exam");
      return;
    }
    toast.success("Exam submitted");
    // Carry the MCQ result along so the result page can show both sections.
    navigate(withNativeParam(`/cbq-exam/${cbqSessionId}/result`), {
      state: { from: backTo, mcqResult },
    });
  };

  // ---------------- shared windows ----------------
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [calcOpen, setCalcOpen] = useState(false);
  const [pdfNonce, setPdfNonce] = useState(0);
  const nativeMode = isNativeMode();
  const openPdf = (url?: string) => {
    if (!url) return;
    setPdfUrl(url);
    setPdfOpen(true);
    // Every tap must behave exactly like the first one. Bumping the nonce
    // remounts the loader, so the iframe re-requests the PDF and the host is
    // messaged again even when the URL is identical — without it, re-tapping
    // the same PDF changes no state and nothing happens.
    setPdfNonce((n) => n + 1);
  };
  const toolButtons = (url?: string, withCalc = true) => (
    <div className="flex flex-col items-start gap-2">
      {url && (
        <button
          type="button"
          onClick={() => openPdf(url)}
          className="p-2 bg-lightGray/10 rounded-lg gap-2 flex items-center sm:text-base text-xs justify-center"
        >
          <PdfQuizIcon /> View Scenario PDF <QuizFolderIcon />
        </button>
      )}
      {withCalc && (
        <>
          <button
            type="button"
            onClick={() => setCalcOpen((v) => !v)}
            className="p-2 bg-lightGray/10 rounded-lg gap-2 flex items-center sm:text-base text-xs justify-center"
          >
            <CalculatorIcon /> Calculator
          </button>
          {/* Under `?isMobile` the calculator is a plain inline block sitting
              above the answer options rather than a floating popup — dragging a
              window is unusable in the host webview. Same button toggles it. */}
          {nativeMode && calcOpen && (
            <div className="w-full">
              <Calculator />
            </div>
          )}
        </>
      )}
    </div>
  );

  // Hold the exam until the CBQ session exists. Showing the questions first
  // would start the clock on an exam the student might not be able to submit.
  if (sessionStarting || (!cbqSessionId && !mcqResult)) {
    return (
      <div className="w-full min-h-[60vh] flex flex-col items-center justify-center gap-4">
        {sessionStarting || retryingSession ? (
          <>
            <Spinner />
            <p className="text-sm text-[#4d4c4d]">Preparing your exam...</p>
          </>
        ) : (
          <>
            <p className="text-sm text-red-600">
              {sessionStartError || "Couldn't start the CBQ section."}
            </p>
            <button
              className="h-10 w-32 rounded bg-[#8cc63f] text-white disabled:opacity-60"
              onClick={retrySession}
              disabled={retryingSession}
            >
              Retry
            </button>
          </>
        )}
      </div>
    );
  }

  // Failed MCQ → just show the MCQ result. Never flash it on a pass.
  if (mcqFailed && mcqResult) {
    return (
      <div className="w-full self-stretch">
        <PostMcqExam submissionResponse={mcqResult} />
      </div>
    );
  }

  const cbqItem = items[cbqCurrent];
  const mcqProgress = Math.round(
    (Object.keys(mcqAnswers).length / Math.max(1, mcqQuestions.length)) * 100
  );
  const cbqQuestionCount = items.filter((i) => i.kind === "question").length;
  const cbqProgress = Math.round(
    (cbqAnswered.size / Math.max(1, cbqQuestionCount)) * 100
  );
  const remaining = section === 1 ? mcqRemaining : cbqRemaining;
  const progress = section === 1 ? mcqProgress : cbqProgress;

  return (
    <div>
      <div className="mx-auto max-w-5xl lg:w-[64rem] flex flex-col">
        {/* Top bar */}
        <div className="bg-[#4d4c4d] sm:h-16 h-12 px-3 flex flex-col justify-center text-white">
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <Link to="/">
                <img className="sm:size-12 size-8" src={Logo} alt="Logo" />
              </Link>
              <h1 className="md:block hidden">
                {section === 1 ? "Section 1 — MCQ" : "Section 2 — CBQ"}
              </h1>
            </div>
            <div className="flex sm:gap-8 gap-4 items-center">
              {remaining !== null && (
                <div className="flex items-center gap-2">
                  <div className="sm:block hidden">
                    <QuizClockIcon height={40} width={40} />
                  </div>
                  <div>
                    <h1 className="md:block hidden">Time Remaining</h1>
                    <p>{formatTime(remaining)}</p>
                  </div>
                </div>
              )}
              <div className="sm:w-32 w-24 text-center">
                <div className="overflow-hidden bg-blue-50 sm:h-3 h-2 rounded-full w-full mb-1">
                  <span
                    className="h-full bg-[#8cc63f] block rounded-full"
                    style={{ width: `${progress}%` }}
                  ></span>
                </div>
                <h1 className="sm:text-base text-xs">Progress {progress}%</h1>
              </div>
              {section === 1 ? (
                <button
                  className="sm:h-10 md:w-36 sm:w-[110px] h-8 w-24 text-sm bg-[#fff8dc] text-black rounded disabled:opacity-60"
                  onClick={
                    mcqSubmitError ? retrySession : () => setConfirmOpen(true)
                  }
                  disabled={submittingMcq || retryingSession}
                >
                  {submittingMcq
                    ? "Submitting..."
                    : retryingSession
                      ? "Retrying..."
                      : mcqSubmitError
                        ? "Retry"
                        : "Finish Section"}
                </button>
              ) : (
                <button
                  className="sm:h-10 md:w-36 sm:w-[100px] h-8 w-20 text-sm bg-[#fff8dc] text-black rounded disabled:opacity-60"
                  onClick={submitCbq}
                  disabled={submittingCbq}
                >
                  {submittingCbq ? "Submitting..." : "Finish Test"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Identity bar */}
        <div className="h-6 bg-[#8cc63f] flex justify-between items-center px-3 text-white sm:text-base text-xs">
          <p>Test: {quiz.title}</p>
          <p className="capitalize">
            Candidate: {userData?.firstName} {userData?.lastName}
          </p>
        </div>

        {/* Body */}
        <div className="bg-white p-1 flex sm:flex-row flex-col h-[85vh]">
          {/* Section tabs */}
          <div className="flex sm:flex-col flex-row gap-3 sm:mr-3 shrink-0 items-center">
            {([1, 2] as const).map((n) => {
              const isCurrent = section === n;
              // Once the MCQ is passed you move on; section 1 can't be revisited.
              const locked = n === 1 ? mcqPassed : !mcqPassed;
              return (
                <button
                  key={n}
                  onClick={() => {
                    if (locked) return;
                    setSection(n);
                  }}
                  disabled={locked}
                  title={
                    locked
                      ? n === 1
                        ? "This section is finished"
                        : "Pass the MCQ section to unlock"
                      : `Section ${n}`
                  }
                  style={{ width: 45, height: 135 }}
                  className={`flex flex-col items-center justify-start gap-1 pt-1 rounded-lg border-2 transition-colors ${
                    locked
                      ? "border-gray-200 opacity-50 cursor-not-allowed"
                      : isCurrent
                      ? "border-[#4d4c4d] bg-gray-50"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <span className="font-bold text-sm leading-none text-[#4d4c4d]">
                    {n}
                  </span>
                  <span
                    style={{ width: 12, height: 96 }}
                    className="rounded-full border border-black bg-gray-300"
                  />
                </button>
              );
            })}
          </div>

          {/* Question navigator */}
          <div className="sm:min-w-[71px] items-center flex sm:flex-col gap-1 text-white w-full sm:overflow-y-scroll sm:pr-2 pb-2 sm:overflow-x-hidden overflow-x-scroll overflow-y-hidden sm:h-auto min-h-12 sm:w-auto">
            {section === 1
              ? mcqQuestions.map((_, i) => {
                  const bg =
                    i === mcqCurrent
                      ? "bg-[#35540d]"
                      : mcqAnswers[i]
                      ? "bg-[#4d4c4d]"
                      : "bg-[#8cc63f]";
                  return (
                    <button
                      key={i}
                      onClick={() => setMcqCurrent(i)}
                      className={`min-w-11 min-h-6 rounded flex justify-center items-center text-xs relative overflow-hidden ${bg}`}
                    >
                      {mcqAnswers[i] && (
                        <span className="absolute top-0 left-0 w-0 h-0 border-t-[10px] border-r-[10px] border-t-white border-r-transparent" />
                      )}
                      {i + 1}
                      {mcqFlagged.has(i) && (
                        <FlagIcon className="absolute right-1" width={14} height={10} />
                      )}
                    </button>
                  );
                })
              : items.map((it, i) => {
                  const isCurrent = i === cbqCurrent;
                  if (it.kind === "scenario") {
                    const viewed = viewedScenarios.has(it.scenario.id);
                    const bg = isCurrent
                      ? "bg-[#35540d]"
                      : viewed
                      ? "bg-[#4d4c4d]"
                      : "bg-[#8cc63f]";
                    return (
                      <button
                        key={`s-${it.scenario.id}`}
                        onClick={() => goToCbqIndex(i)}
                        className={`relative min-w-11 min-h-6 rounded flex justify-center items-center overflow-hidden ${bg}`}
                      >
                        {viewed && (
                          <span className="absolute top-0 left-0 w-0 h-0 border-t-[10px] border-r-[10px] border-t-white border-r-transparent" />
                        )}
                        <span
                          className={`flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold leading-none ${
                            viewed
                              ? "bg-white text-[#4d4c4d]"
                              : "border border-white text-white"
                          }`}
                        >
                          i
                        </span>
                      </button>
                    );
                  }
                  const bg = isCurrent
                    ? "bg-[#35540d]"
                    : cbqAnswered.has(it.q.id)
                    ? "bg-[#4d4c4d]"
                    : "bg-[#8cc63f]";
                  return (
                    <button
                      key={`q-${it.q.id}`}
                      onClick={() => goToCbqIndex(i)}
                      className={`min-w-11 min-h-6 rounded flex justify-center items-center text-xs relative overflow-hidden ${bg}`}
                    >
                      {cbqAnswered.has(it.q.id) && (
                        <span className="absolute top-0 left-0 w-0 h-0 border-t-[10px] border-r-[10px] border-t-white border-r-transparent" />
                      )}
                      {it.qNumber}
                      {cbqFlagged.has(it.q.id) && (
                        <FlagIcon className="absolute right-1" width={14} height={10} />
                      )}
                    </button>
                  );
                })}
          </div>

          {/* Main panel */}
          <div className="sm:p-6 p-3 border border-[#9EA7AC] rounded-md w-full h-full overflow-y-scroll">
            {section === 1 ? (
              <>
                <p className="bg-[#F5F7F8] p-2.5 mb-4 sm:text-base text-sm text-[#333]">
                  {mcqQuestions[mcqCurrent]?.question}
                </p>
                <div className="space-y-3">
                  {(
                    mcqQuestions[mcqCurrent]?.answers ??
                    mcqQuestions[mcqCurrent]?.choices ??
                    []
                  ).map((c, ci) => {
                    const selected = mcqAnswers[mcqCurrent] === c.id;
                    return (
                      <div key={c.id} className="flex items-center gap-3">
                        <span className="font-semibold text-burntBlack w-5 shrink-0">
                          {letter(ci)}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setMcqAnswers((p) => ({ ...p, [mcqCurrent]: c.id }))
                          }
                          className={`flex-1 text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                            selected
                              ? "bg-[#FFE7C2] border-[#E8890C]"
                              : "bg-white border-gray-300 hover:border-gray-400"
                          }`}
                        >
                          {c.text ?? c.answer}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : !cbq || cbqLoading ? (
              <div className="h-full flex items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <>
                {cbqItem?.kind === "scenario" && (
                  <div>
                    <p className="bg-[#F5F7F8] p-2.5 mb-3 sm:text-base text-sm text-[#333]">
                      Scenario {cbqItem.scenarioIndex + 1}: {cbqItem.scenario.title}
                    </p>
                    {cbqItem.scenario.description ? (
                      <p className="bg-[#F5F7F8] p-2.5 mb-3 sm:text-base text-sm text-[#333] whitespace-pre-line">
                        {cbqItem.scenario.description}
                      </p>
                    ) : null}
                    {toolButtons(cbqItem.scenario.pdfUrl, false)}
                  </div>
                )}
                {items.map((it, i) =>
                  it.kind === "question" ? (
                    <div
                      key={`qp-${it.q.id}`}
                      className={i === cbqCurrent ? "" : "hidden"}
                    >
                      <CbqQuestion
                        question={it.q}
                        saved={savedByQuestion[it.q.id]}
                        onSave={(body) => saveCbq(it.q.id, body)}
                        belowPrompt={toolButtons(it.scenario.pdfUrl)}
                      />
                    </div>
                  ) : null
                )}
              </>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="bg-[#4d4c4d] h-14 px-3 flex items-center justify-end gap-2 text-white">
          {section === 1 ? (
            <>
              <button
                onClick={() =>
                  setMcqFlagged((prev) => {
                    const n = new Set(prev);
                    n.has(mcqCurrent) ? n.delete(mcqCurrent) : n.add(mcqCurrent);
                    return n;
                  })
                }
                className={`h-10 w-10 rounded flex items-center justify-center ${
                  mcqFlagged.has(mcqCurrent) ? "bg-[#eab326]" : "bg-[#8cc63f]"
                }`}
                title="Flag for review"
              >
                <FlagIcon />
              </button>
              <button
                className="h-10 w-20 rounded bg-[#8cc63f] flex items-center gap-2 justify-center disabled:opacity-50"
                onClick={() => setMcqCurrent((i) => Math.max(0, i - 1))}
                disabled={mcqCurrent === 0}
              >
                <QuizArrowIcon className="rotate-180" /> Back
              </button>
              {mcqCurrent < mcqQuestions.length - 1 ? (
                <button
                  className="h-10 w-20 rounded bg-[#8cc63f] flex items-center gap-2 justify-center"
                  onClick={() =>
                    setMcqCurrent((i) => Math.min(mcqQuestions.length - 1, i + 1))
                  }
                >
                  Next <QuizArrowIcon />
                </button>
              ) : (
                <button
                  className="h-10 w-32 rounded bg-[#fff8dc] text-black flex items-center justify-center disabled:opacity-60"
                  onClick={
                    mcqSubmitError ? retrySession : () => setConfirmOpen(true)
                  }
                  disabled={submittingMcq || retryingSession}
                >
                  {retryingSession
                    ? "Retrying..."
                    : mcqSubmitError
                      ? "Retry"
                      : "Finish Section"}
                </button>
              )}
            </>
          ) : (
            <>
              {cbqItem?.kind === "question" && (
                <button
                  onClick={() => {
                    const qid = cbqItem.q.id;
                    const n = new Set(cbqFlagged);
                    const will = !n.has(qid);
                    will ? n.add(qid) : n.delete(qid);
                    setCbqFlagged(n);
                    SaveCBQResponse(cbqSessionId, qid, {
                      type: cbqItem.q.type,
                      isFlagged: will,
                    });
                  }}
                  className={`h-10 w-10 rounded flex items-center justify-center ${
                    cbqFlagged.has(cbqItem.q.id) ? "bg-[#eab326]" : "bg-[#8cc63f]"
                  }`}
                  title="Flag for review"
                >
                  <FlagIcon />
                </button>
              )}
              <button
                className="h-10 w-20 rounded bg-[#8cc63f] flex items-center gap-2 justify-center disabled:opacity-50"
                onClick={() => goToCbqIndex(Math.max(0, cbqCurrent - 1))}
                disabled={cbqCurrent === 0}
              >
                <QuizArrowIcon className="rotate-180" /> Back
              </button>
              {cbqCurrent < items.length - 1 ? (
                <button
                  className="h-10 w-20 rounded bg-[#8cc63f] flex items-center gap-2 justify-center"
                  onClick={() =>
                    goToCbqIndex(Math.min(items.length - 1, cbqCurrent + 1))
                  }
                >
                  Next <QuizArrowIcon />
                </button>
              ) : (
                <button
                  className="h-10 w-28 rounded bg-[#fff8dc] text-black flex items-center justify-center disabled:opacity-60"
                  onClick={submitCbq}
                  disabled={submittingCbq}
                >
                  Finish Test
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <FinishSectionModal
        open={confirmOpen}
        onConfirm={() => {
          setConfirmOpen(false);
          submitMcq();
        }}
        onCancel={() => setConfirmOpen(false)}
      />

      {pdfOpen && nativeMode && (
        <HiddenPdfLoader
          key={pdfNonce}
          src={pdfUrl}
          title="Scenario PDF"
        />
      )}

      {pdfOpen && !nativeMode && (
        <Draggable handle=".cbq-pdf-header">
          <div
            className="fixed top-24 left-8 z-[9999] flex flex-col bg-white border border-black/20 rounded-lg shadow-2xl overflow-hidden"
            style={{ width: 680, height: 560, resize: "both" }}
          >
            <div className="cbq-pdf-header cursor-move bg-[#4d4c4d] text-white px-3 py-2 flex items-center justify-between select-none">
              <span className="text-sm font-semibold">Scenario PDF</span>
              <button type="button" onClick={() => setPdfOpen(false)} title="Close">
                <CloseIcon fill="#fff" />
              </button>
            </div>
            <iframe
              src={pdfUrl}
              title="Scenario PDF"
              className="flex-1 w-full border-0"
            />
          </div>
        </Draggable>
      )}

      {calcOpen && !nativeMode && (
        <Draggable handle=".cbq-calc-header" cancel=".cbq-calc-close">
          <div className="fixed top-32 left-16 z-[9999] bg-white border border-black/20 rounded-lg shadow-2xl overflow-hidden">
            <div className="cbq-calc-header cursor-move bg-[#4d4c4d] text-white px-3 py-2 flex items-center justify-between select-none">
              <span className="text-sm font-semibold">Calculator</span>
              {/* cbq-calc-close is in Draggable's `cancel` selector: the header
                  is the drag handle, and react-draggable preventDefault()s
                  touchstart inside it, so without this the tap never becomes a
                  click and the button is dead on mobile. */}
              <button
                type="button"
                className="cbq-calc-close"
                onClick={() => setCalcOpen(false)}
                title="Close"
              >
                <CloseIcon fill="#fff" />
              </button>
            </div>
            <Calculator />
          </div>
        </Draggable>
      )}
    </div>
  );
};

export default MergedMcqCbqExam;
